import React, { useEffect, useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { ReceivingMethodContainer } from './styles';
import { ReceivingMethodPropsType } from './utils';
import { reduxForm, getFormValues } from 'redux-form';
import { IStore, AppDispatch } from '../../../../store.type';
import { CommonButton } from '@Components';
import {
  getListOfCountries,
  getPopularCountriesFromCMS,
  renderReceivingMethodFields
} from './utils/methods';
import {
  navigateHistory,
  setBeneBankNameAction,
  setBeneRevampFormValues
} from '@Redux/actions/new-bene-flow/new-bene-flow-action';
import { ReceivingMethodFormValueType } from './utils/types/types';
import { getCountryCode, selectNewBeneState } from '@Redux/selectors';
import { PayoutMethodDropdown } from './components/payout-method-dropdown/PayoutMethodDropdown';
import { HEADING } from './utils/constants';
import { BeneficiaryFlowState, ChildComponentOwnPropsType } from '../../types';
import { RECEIVING_METHOD_FORM_NAME, screenNames } from '../../constants';
import { activateLoader, disableLoader, scrollToTop } from '@Redux/actions/ui-trigger';

const ReceivingMethodForm: React.FC<ReceivingMethodPropsType> = ({
  handleSubmit,
  initialize,
  parentState: { isEditFlow, payoutMethods, beneState }
}: ReceivingMethodPropsType) => {
  const dropdownRef = useRef<HTMLInputElement | undefined>();
  const dispatch = useDispatch<AppDispatch>();
  const formValues = useSelector((state: IStore) =>
    getFormValues(RECEIVING_METHOD_FORM_NAME)(state)
  ) as ReceivingMethodFormValueType;

  const {
    receivingMethodBankAccountCountryValues,
    formValues: currentFlowFormValues,
    popularCountries,
    popularCountriesFetched
  } = useSelector(selectNewBeneState) as BeneficiaryFlowState;

  const { currentBene, isBeneSchemaEnabled: isBeneSchemaApplicable } = useSelector(
    selectNewBeneState
  ) as BeneficiaryFlowState;

  const userAccountCountry = useSelector(getCountryCode);

  const init = async () => {
    if (isEditFlow) {
      dispatch(activateLoader());
      initialize({
        account_type: currentBene?.account_type || currentBene?.beneficiary_account_type || '',
        bank_account_country:
          currentBene?.bank_account_country || currentBene?.destination_country || '',
        currency_code: currentBene?.currency_code || currentBene?.destination_currency || '',
        payout_method: currentBene?.payout_method || ''
      });

      await getListOfCountries(
        currentBene?.account_type || currentBene?.beneficiary_account_type,
        dispatch,
        popularCountries,
        userAccountCountry
      );
      dispatch(disableLoader());

      // this is for incase user decides to go back and edit the receiving method again
    } else if (currentFlowFormValues) {
      initialize({
        account_type: currentFlowFormValues?.account_type || '',
        bank_account_country: currentFlowFormValues?.bank_account_country || '',
        currency_code: currentFlowFormValues?.currency_code || '',
        payout_method: currentFlowFormValues?.payout_method || ''
      });
    }
  };

  const submitForm = (values: any) => {
    const payoutMethod: string =
      values.payout_method === 'LOCAL' ? 'bank' : values.payout_method?.toLowerCase();
    dispatch(
      setBeneRevampFormValues({
        ...values,
        details_selected: isBeneSchemaApplicable ? values.payout_method : payoutMethod
      })
    );
    dispatch(
      navigateHistory({
        type: 'PUSH_NAVIGATION_HISTORY',
        screen: screenNames.personal_details.name
      })
    );
    dispatch(setBeneBankNameAction(null));
    dispatch(scrollToTop());
  };

  useEffect(() => {
    init();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentBene]);

  useEffect(() => {
    if (!popularCountriesFetched) {
      getPopularCountriesFromCMS(dispatch);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <ReceivingMethodContainer>
      <div className="child-component-container__content-container">
        <form onSubmit={handleSubmit(submitForm)}>
          <div className="content-container__fields">
            {renderReceivingMethodFields(
              formValues,
              dispatch,
              dropdownRef,
              receivingMethodBankAccountCountryValues,
              popularCountries,
              isEditFlow || currentBene?.has_multiple_payout,
              userAccountCountry
            )}
            {formValues?.currency_code && payoutMethods?.length > 1 && (
              <>
                <div className="section-heading">{HEADING}</div>
                <PayoutMethodDropdown
                  country_code={formValues?.bank_account_country}
                  currency_code={formValues?.currency_code}
                  account_type={formValues?.account_type}
                  paymentMethods={payoutMethods}
                  beneState={beneState}
                  isEditFlow={isEditFlow}
                />
              </>
            )}
            <CommonButton margin="24px 0 0 0" text="Next" width={450} />
          </div>
        </form>
      </div>
    </ReceivingMethodContainer>
  );
};

const ReceivingMethod = reduxForm<ReceivingMethodFormValueType, ChildComponentOwnPropsType>({
  form: RECEIVING_METHOD_FORM_NAME
})(ReceivingMethodForm);
export default ReceivingMethod;
